import Vue from '@/externals/vue';
import t from './types';

export default {
  [t.HASH]: (state, hash) => {
    state.hash = hash;
  },
  [t.RESET]: state => {
    state.events = [];
    state.selections = [];
    state.status = t.STARTED;
    state.currentPreset = '';
    state.currentSort = t.SORT_TIME;

    Object.entries(state.fields).forEach(([id, field]) => {
      Vue.set(state.fields, id, {
        ...field,
        metric: field.defaultMetric,
      });
    });

    Object.entries(state.metrics).forEach(([id, metric]) => {
      Vue.set(state.metrics, id, {
        ...metric,
        value: metric.defaultValue,
      });
    });
  },

  [t.QUERY]: (state, query) => {
    state.query = query;
  },

  [t.EVENT_FILTER]: (state, eventId) => {
    state.eventFilter = eventId;
  },

  [t.EVENT_RUNNERS]: (state, eventRunners) => {
    state.eventRunners = eventRunners;
  },

  [t.FIELDS]: (state, filters) => {
    state.fields = {};
    state.categories = [];
    state.metrics = {};
    const groups = new Map();
    filters.categories.forEach(({ name, displayName, order, fields }) => {
      state.categories.push({
        id: name,
        name: displayName,
        order,
      });

      fields.forEach(field => {
        const subFields = field.subFields || [field];
        const hasSubFields = subFields.length > 1;

        subFields.forEach(subField => {
          const groupBy = hasSubFields ? field : subField;
          const groupById = `${groupBy.name}.${groupBy.defaultMetric}.${groupBy.defaultRange}`;
          if (!groups.has(groupById)) {
            groups.set(groupById, {
              id: groupById,
              name: groupBy.displayName,
            });
          }
          let order = 0;
          groupBy.metrics.forEach(metric => {
            order++;
            // check for any 'active' filters which will be present if a hash was used
            if (metric.name) {
              let metricValue = metric.defaultValue;
              if (filters.hasOwnProperty('activeFilters') && filters.activeFilters !== null) {
                filters.activeFilters.forEach(activeFilter => {
                  if (
                    activeFilter.name === groupBy.name &&
                    activeFilter.range === groupBy.defaultRange &&
                    activeFilter.metric === metric.name
                  ) {
                    metricValue = activeFilter.value;
                  }
                });
              }

              const defaultRange = metric.defaultRange ? metric.defaultRange : groupBy.defaultRange;

              const groupId = metric.param
                ? `${metric.param}.${metric.name}.${defaultRange}`
                : `${groupBy.name}.${metric.name}.${defaultRange}`;
              const metric_setting = metric.param ? metric.param : groupBy.name;

              metric.order = order;

              Vue.set(state.metrics, `${groupId}`, {
                id: `${groupId}`,
                label: metric.displayName,
                range: defaultRange,
                setting: metric_setting,
                value: metricValue,
                ...metric,
              });
            }
          });
          // rekey metric so it's unique per field
          groupBy.defaultMetric = `${groupBy.name}.${groupBy.defaultMetric}.${groupBy.defaultRange}`;

          const metricList = groupBy.metrics.map(metric => {
            if (metric.name) {
              const defaultRange = metric.defaultRange ? metric.defaultRange : groupBy.defaultRange;
              return metric.param
                ? `${metric.param}.${metric.name}.${defaultRange}`
                : `${groupBy.name}.${metric.name}.${defaultRange}`;
            }
          });

          Vue.set(state.fields, `${subField.defaultMetric}`, {
            name: subField.name,
            order: subField.order,
            label: subField.displayName,
            category: name,
            group: {
              id: groupById,
              name: groupBy.displayName,
            },
            metrics: metricList,
            defaultMetric: groupBy.defaultMetric,
            metric: groupBy.defaultMetric,
            metricList: [groupBy.defaultMetric],
            range: groupBy.defaultRange,
            description: groupBy.description || '',
          });
        });
      });
    });

    state.groups = Array.from(groups.values());
  },

  [t.INFO]: (state, info) => {
    state.info = info;
  },

  [t.PRESET]: (state, currentPreset) => {
    state.currentPreset = currentPreset;

    if (currentPreset === '') {
      return;
    }

    state.presets.forEach(preset => {
      if (preset.name === currentPreset) {
        preset.filters.forEach(filter => {
          const currentFields = [];
          Object.entries(state.fields).map(field => {
            if (field[1].name === `${filter.name}`) {
              currentFields.push(field[1]);
            }
          });
          const metric = `${filter.name}.${filter.metric}.${filter.range}`;

          if (currentFields) {
            currentFields.forEach(currentField => {
              const metricList = currentField.metricList;
              //console.log(metric);
              const updatedMetricList = replaceRateMetricIfExists(metricList, metric);
              if (!updatedMetricList.includes(metric)) {
                updatedMetricList.push(metric);
              }
            });
          }
          Vue.set(state.metrics[metric], 'value', filter.value);
        });
      }
    });

    state.presetData.forEach(preset => {
      if (preset.name === currentPreset) {
        preset.options.filters.forEach(filter => {
          const currentFields = [];
          Object.entries(state.fields).map(field => {
            if (field[1].name === `${filter.name}`) {
              currentFields.push(field[1]);
            }
          });

          const metric = `${filter.name}.${filter.metric}.${filter.range}`;
          if (currentFields) {
            currentFields.forEach(currentField => {
              const metricList = currentField.metricList;
              const updatedMetricList = replaceRateMetricIfExists(metricList, metric);
              if (!updatedMetricList.includes(metric)) {
                updatedMetricList.push(metric);
              }
            });
          }

          Vue.set(state.metrics[metric], 'value', filter.value);
        });
      }
    });
  },

  [t.PRESETS]: (state, presets) => {
    state.presets = presets;
  },

  [t.RESULTS]: (state, { events }) => {
    state.events = events;
    state.hasSearched = true;
  },

  [t.SORT]: (state, sort) => {
    state.currentSort = sort;
  },

  [t.DATE_FILTER]: (state, date) => {
    state.dateFilter = date;
  },

  [t.STATUS]: (state, status) => {
    state.status = status;
  },

  [t.TOTAL_COUNT]: (state, [count, dayRange]) => {
    state.count = count;
    state.dayRange = dayRange;
  },

  [t.FIELD]: (state, [filter, metric, value, range]) => {
    state.currentPreset = '';
    Vue.set(state.metrics, metric, {
      ...state.metrics[metric],
      value,
      range,
    });
    const metricList = state.fields[filter].metricList;
    // replace 'winRate' with 'placeRate' or vice versa, then add metric if doesn't exist in list
    const updatedMetricList = replaceRateMetricIfExists(metricList, metric);
    if (!updatedMetricList.includes(metric)) {
      updatedMetricList.push(metric);
    }
    Vue.set(state.fields, filter, {
      ...state.fields[filter],
      metric,
      metricList: updatedMetricList,
    });
  },
  SET_MODAL_STATUS: (state, { showModal }) => {
    state.showModal = showModal;
  },
  SET_LOADING_STATUS: (state, { isLoading }) => {
    state.isLoading = isLoading;
  },
  SET_PRESET_DATA: (state, { presetData }) => {
    state.presetData = presetData;
  },
  SET_LOGIN_STATUS: (state, { isLoggedIn }) => {
    state.isLoggedIn = isLoggedIn;
  },
};

function replaceRateMetricIfExists(metricList, metric) {
  metricList.map((listedMetric, index) => {
    if (isWinPlaceRateMetric(listedMetric) && isWinPlaceRateMetric(metric)) {
      metricList[index] = metric;
    }
  });

  return metricList;
}

function isWinPlaceRateMetric(metric) {
  const metricStringList = metric.split('.');
  return metricStringList[1] === 'winRate' || metricStringList[1] === 'placeRate' ? true : false;
}
